// ABOUTME: Shared loading placeholder for custom page widgets, with one shape variant per widget kind.
// ABOUTME: Renders design-system Skeletons sized to the loaded content so the widget box does not shift.

import { alpha, Box, Card, Skeleton, Stack, styled } from '@ds/coolshop';
import { useIntl } from 'react-intl';

// One animation across every custom-page skeleton, so the whole page breathes together. `wave`
// builds its shimmer from the barely perceptible action.hover colour and adds a mask image per
// element; `pulse` animates opacity only.
const SKELETON_ANIMATION = 'pulse' as const;

// The frame paints an admin-configured bgColor and textColor on the same container, so the
// placeholder fill is derived from the inherited text colour instead of a fixed grey: it stays
// visible on a dark bgColor and follows the palette when the theme changes. The doubled selector
// outranks the design system's own Skeleton class, whose position in the stylesheet depends on
// which of the two components mounts first.
const FrameSkeleton = styled(Skeleton)(({ theme }) => ({
    '&&': {
        backgroundColor: alpha(theme.palette.text.primary, 0.11),
        '@supports (background-color: color-mix(in srgb, currentColor 13%, transparent))': {
            backgroundColor: 'color-mix(in srgb, currentColor 13%, transparent)',
        },
    },
}));

export type WidgetSkeletonVariant =
    | 'table'
    | 'cards'
    | 'fields'
    | 'chart'
    | 'matrix'
    | 'tree'
    | 'block';

// Widget types whose loading state is a framed placeholder, used by the layout renderer to pick a
// Suspense fallback while the widget chunk downloads. `action-bar` and `node-editor` are absent on
// purpose: neither renders a WidgetFrame once loaded, so a framed fallback would appear and vanish.
export const WIDGET_TYPE_SKELETON_VARIANT: Record<string, WidgetSkeletonVariant> = {
    'node-list': 'table',
    'linked-items': 'table',
    'linked-by-items': 'table',
    'favorite-nodes': 'table',
    'node-tree': 'tree',
    'node-info': 'fields',
    chart: 'chart',
    matrix: 'matrix',
    'model-selection': 'block',
    'card-grid': 'cards',
    // Title and a few lines: the fields placeholder, with no fixed height to collapse from.
    text: 'fields',
    'completeness-overview': 'chart',
    'incomplete-nodes': 'table',
    'translation-coverage': 'table',
    'review-publish': 'table',
};

// Enough rows to read as a list without spending an element per loaded row: the row placeholders
// share the available height, so the count only sets how finely the box is striped.
const DEFAULT_ROW_COUNT = 6;
const DEFAULT_CARD_COUNT = 5;
const DEFAULT_FIELD_COUNT = 5;
const DEFAULT_TREE_ROW_COUNT = 8;

// A data table header and one of its rows.
const TABLE_HEADER_HEIGHT = 40;
const TABLE_ROW_HEIGHT = 40;
// Mirrors ChartWidget's chart box.
export const CHART_HEIGHT = 350;
// Mirror VirtualizedMatrix's column header strip and its default height.
const MATRIX_HEADER_HEIGHT = 52;
const MATRIX_MIN_HEIGHT = '70vh';
// Matches the Avatar of a NodeInfoWidget field row.
const FIELD_ICON_SIZE = 30;
// The `caption` label and the `subtitle2` value of a NodeInfoWidget field row.
const FIELD_LABEL_FONT_SIZE = '0.75rem';
const FIELD_VALUE_FONT_SIZE = '0.875rem';

// A tree reads as nested rows of uneven length, so the bars cycle through a few widths and indents.
const TREE_ROWS = [
    { width: '70%', indent: 0 },
    { width: '55%', indent: 2 },
    { width: '85%', indent: 2 },
];

interface WidgetSkeletonProps {
    variant: WidgetSkeletonVariant;
    // Placeholder rows/cards/fields. Defaults match each widget's loaded default.
    count?: number;
    // Card flow, mirroring CardViewConfig.direction.
    direction?: 'row' | 'column';
    // Card box, mirroring CardViewConfig.cardWidth / cardHeight.
    cardWidth?: string;
    cardHeight?: string;
    // Height the placeholder claims when its container is not already height-constrained.
    minHeight?: string | number;
}

// Distinct keys for a list of shapes that has no data behind it.
const placeholderKeys = (length: number): string[] =>
    Array.from({ length }, (_, index) => `placeholder-${index}`);

export function WidgetSkeleton({
    variant,
    count,
    direction = 'column',
    cardWidth,
    cardHeight,
    minHeight,
}: WidgetSkeletonProps) {
    const { formatMessage } = useIntl();

    // Announced once per widget, on the container the placeholder shapes live in.
    const busyProps = {
        role: 'status',
        'aria-busy': true,
        'aria-live': 'polite' as const,
        'aria-label': formatMessage({
            id: 'custom-pages.loading',
            defaultMessage: 'Loading…',
        }),
    };

    if (variant === 'cards') {
        return (
            <Stack
                {...busyProps}
                direction={direction}
                flexWrap={direction === 'row' ? 'wrap' : undefined}
                gap={1}
            >
                {placeholderKeys(count ?? DEFAULT_CARD_COUNT).map(key => (
                    <FrameSkeleton
                        key={key}
                        animation={SKELETON_ANIMATION}
                        variant="rounded"
                        width={cardWidth}
                        height={cardHeight ?? 72}
                    />
                ))}
            </Stack>
        );
    }

    if (variant === 'fields') {
        return (
            <Stack {...busyProps} gap={0}>
                {placeholderKeys(count ?? DEFAULT_FIELD_COUNT).map(key => (
                    <Box key={key} display="flex" alignItems="center" gap={1.5} py={1}>
                        <FrameSkeleton
                            animation={SKELETON_ANIMATION}
                            variant="circular"
                            width={FIELD_ICON_SIZE}
                            height={FIELD_ICON_SIZE}
                        />
                        {/* `text` skeletons sized with the row's font sizes reproduce its exact
                            line boxes, so the placeholder row is as tall as the loaded one. */}
                        <Stack flex={1} minWidth={0} py={0.5} gap={0}>
                            <FrameSkeleton
                                animation={SKELETON_ANIMATION}
                                variant="text"
                                fontSize={FIELD_LABEL_FONT_SIZE}
                                width="35%"
                            />
                            <FrameSkeleton
                                animation={SKELETON_ANIMATION}
                                variant="text"
                                fontSize={FIELD_VALUE_FONT_SIZE}
                                width="70%"
                            />
                        </Stack>
                    </Box>
                ))}
            </Stack>
        );
    }

    if (variant === 'chart') {
        return (
            <Box {...busyProps}>
                <Card padding={2}>
                    <FrameSkeleton
                        animation={SKELETON_ANIMATION}
                        variant="rounded"
                        width="100%"
                        height={CHART_HEIGHT}
                    />
                </Card>
            </Box>
        );
    }

    if (variant === 'tree') {
        return (
            <Stack {...busyProps} gap={1} padding={1}>
                {placeholderKeys(count ?? DEFAULT_TREE_ROW_COUNT).map((key, index) => {
                    const row = TREE_ROWS[index % TREE_ROWS.length];
                    return (
                        <Box key={key} paddingLeft={row.indent}>
                            <FrameSkeleton
                                animation={SKELETON_ANIMATION}
                                variant="text"
                                fontSize={FIELD_VALUE_FONT_SIZE}
                                width={row.width}
                            />
                        </Box>
                    );
                })}
            </Stack>
        );
    }

    if (variant === 'block') {
        return (
            <Box {...busyProps} height={minHeight ?? '100%'}>
                <FrameSkeleton
                    animation={SKELETON_ANIMATION}
                    variant="rounded"
                    width="100%"
                    height="100%"
                />
            </Box>
        );
    }

    if (variant === 'matrix') {
        return (
            <Box
                {...busyProps}
                display="flex"
                flexDirection="column"
                gap={1}
                minHeight={minHeight ?? MATRIX_MIN_HEIGHT}
            >
                <FrameSkeleton
                    animation={SKELETON_ANIMATION}
                    variant="rounded"
                    height={MATRIX_HEADER_HEIGHT}
                />
                <FrameSkeleton animation={SKELETON_ANIMATION} variant="rounded" flex={1} />
            </Box>
        );
    }

    // `table`: a header bar plus rows that share whatever height the frame gives them, so the
    // same shape fills a capped widget, a full-height column or an explicit minHeight.
    return (
        <Box
            {...busyProps}
            display="flex"
            flexDirection="column"
            gap={1}
            padding={1}
            height="100%"
            minHeight={minHeight}
        >
            <FrameSkeleton
                animation={SKELETON_ANIMATION}
                variant="rounded"
                height={TABLE_HEADER_HEIGHT}
            />
            {/* A row is a table row tall and grows from there, so the placeholder fills a frame
                that caps its height and still reserves a table-sized box in one that does not. */}
            {placeholderKeys(count ?? DEFAULT_ROW_COUNT).map(key => (
                <FrameSkeleton
                    key={key}
                    animation={SKELETON_ANIMATION}
                    variant="rounded"
                    height={TABLE_ROW_HEIGHT}
                    flex="1 1 auto"
                />
            ))}
        </Box>
    );
}
